import React, { useState, useRef } from 'react';
import { Upload, FileText, BarChart3, TrendingUp, AlertCircle, CheckCircle2, X, Download, Loader } from 'lucide-react';
import apiRequest from '../services/api';
import * as i18nModule from '../services/i18n';

const t = i18nModule.t || ((key: string) => key);

interface GSCAnalysisResult {
  summary: {
    totalKeywords: number;
    totalPages: number;
    totalClicks: number;
    totalImpressions: number;
    averageCTR: string;
    averagePosition: string;
    topDevice: string;
    topCountry: string;
  };
  keywordAnalysis: any[];
  pageAnalysis: any[];
  deviceAnalysis: any[];
  countryAnalysis: any[];
  aiInsights: any;
  recommendations: any[];
}

export const GSCAnalyzer: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<GSCAnalysisResult | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'recommendations' | 'keywords' | 'pages' | 'insights' | 'seoGuide'>('overview');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files) {
      const files = Array.from(e.dataTransfer.files).filter(file => file.name.endsWith('.csv'));
      if (files.length > 0) {
        setSelectedFiles(prev => [...prev, ...files]);
        setError(null);
      } else {
        setError(t('gsc.invalidFile') || 'Please upload CSV files');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).filter(file => file.name.endsWith('.csv'));
      if (files.length > 0) {
        setSelectedFiles(prev => [...prev, ...files]);
        setError(null);
      } else {
        setError(t('gsc.invalidFile') || 'Please upload CSV files');
      }
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) {
      setError(t('gsc.noFile') || 'Please upload at least one CSV file');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisResult(null);

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('csvFiles', file);
    });

    try {
      const result = await apiRequest<{ success: boolean; data: GSCAnalysisResult }>('/gsc/analyze-gsc', {
        method: 'POST',
        body: formData,
        timeout: 120000, // 2 minutes
      });

      if (result.success) {
        setAnalysisResult(result.data);
        setActiveTab('overview');
      } else {
        setError(t('gsc.analysisFailed') || 'Analysis failed');
      }
    } catch (err: any) {
      console.error('GSC Analysis error:', err);
      setError(err.message || t('gsc.uploadFailed') || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setAnalysisResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2 flex items-center gap-3">
            <BarChart3 className="text-indigo-400" size={36} />
            {(() => {
              const title = t('gsc.title');
              return title && title !== 'gsc.title' ? title : 'Google Search Console Analyzer';
            })()}
          </h1>
          <p className="text-slate-400 text-sm sm:text-base">
            {(() => {
              const subtitle = t('gsc.subtitle');
              return subtitle && subtitle !== 'gsc.subtitle' ? subtitle : 'Upload your GSC CSV files directly. AI will automatically detect file types and provide comprehensive analysis with SEO recommendations.';
            })()}
          </p>
        </div>

        {/* File Upload Section */}
        {!analysisResult && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8 mb-6">
            <div
              className={`border-2 border-dashed rounded-xl p-8 sm:p-12 text-center transition-all cursor-pointer ${
                dragActive
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-slate-600 hover:border-indigo-400 hover:bg-slate-800/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />

              <Upload className="mx-auto mb-4 text-indigo-400" size={48} />
              <h3 className="text-xl font-bold text-white mb-2">
                {(() => {
                  const text = t('gsc.dropFiles');
                  return text && text !== 'gsc.dropFiles' ? text : 'Drop your GSC CSV files here';
                })()}
              </h3>
              <p className="text-slate-400 mb-4">
                {(() => {
                  const text = t('gsc.clickToBrowse');
                  return text && text !== 'gsc.clickToBrowse' ? text : 'or click to browse (you can upload multiple files)';
                })()}
              </p>

              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={18} />
                          <span className="text-emerald-300 font-medium truncate">{file.name}</span>
                          <span className="text-slate-400 text-xs flex-shrink-0">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="text-slate-400 hover:text-red-400 flex-shrink-0 ml-2"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 bg-slate-900/50 rounded-xl p-4 border border-slate-700">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <FileText size={16} />
                {(() => {
                  const text = t('gsc.uploadInstructions');
                  return text && text !== 'gsc.uploadInstructions' ? text : 'Upload Instructions:';
                })()}
              </h4>
              <p className="text-sm text-slate-300 mb-3">
                {(() => {
                  const text = t('gsc.uploadDescription');
                  return text && text !== 'gsc.uploadDescription' ? text : 'Upload your CSV files directly from Google Search Console. Our AI will automatically detect what each file contains (queries, pages, countries, devices, etc.) and analyze them accordingly.';
                })()}
              </p>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>✓ {(() => {
                  const text = t('gsc.queriesFile');
                  return text && text !== 'gsc.queriesFile' ? text : 'Queries.csv - Search queries with clicks, impressions, CTR';
                })()}</li>
                <li>✓ {(() => {
                  const text = t('gsc.pagesFile');
                  return text && text !== 'gsc.pagesFile' ? text : 'Pages.csv - Page performance data';
                })()}</li>
                <li>✓ {(() => {
                  const text = t('gsc.countriesFile');
                  return text && text !== 'gsc.countriesFile' ? text : 'Countries.csv - Geographic performance';
                })()}</li>
                <li>✓ {(() => {
                  const text = t('gsc.devicesFile');
                  return text && text !== 'gsc.devicesFile' ? text : 'Devices.csv - Device breakdown';
                })()}</li>
                <li className="text-slate-500 text-xs mt-2">
                  {(() => {
                    const text = t('gsc.aiDetection');
                    return text && text !== 'gsc.aiDetection' ? text : '💡 AI automatically detects file types - no need to name them correctly!';
                  })()}
                </li>
              </ul>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
                <AlertCircle className="text-red-400" size={20} />
                <span className="text-red-300">{error}</span>
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={selectedFiles.length === 0 || loading}
              className="mt-6 w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  {(() => {
                    const text = t('gsc.analyzing');
                    return text && text !== 'gsc.analyzing' ? text : 'Analyzing...';
                  })()}
                </>
              ) : (
                <>
                  <TrendingUp size={20} />
                  {(() => {
                    const text = t('gsc.analyzeButton');
                    return text && text !== 'gsc.analyzeButton' ? text : `Analyze ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`;
                  })()}
                </>
              )}
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <Loader className="animate-spin mx-auto mb-4 text-indigo-400" size={48} />
            <p className="text-slate-400">{(() => {
              const text = t('gsc.analyzingWithAI');
              return text && text !== 'gsc.analyzingWithAI' ? text : 'Analyzing your data with AI...';
            })()}</p>
          </div>
        )}

        {/* Analysis Results */}
        {analysisResult && !loading && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
            {/* Report Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6 sm:p-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    {(() => {
                      const text = t('gsc.analysisReport');
                      return text && text !== 'gsc.analysisReport' ? text : 'Analysis Report';
                    })()}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        analysisResult.aiInsights?.overallHealth === 'excellent'
                          ? 'bg-emerald-500 text-white'
                          : analysisResult.aiInsights?.overallHealth === 'good'
                          ? 'bg-blue-500 text-white'
                          : analysisResult.aiInsights?.overallHealth === 'needs_improvement'
                          ? 'bg-amber-500 text-white'
                          : 'bg-red-500 text-white'
                      }`}
                    >
                      {(analysisResult.aiInsights?.overallHealth || 'ANALYZED').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-all"
                >
                  {(() => {
                    const text = t('gsc.newAnalysis');
                    return text && text !== 'gsc.newAnalysis' ? text : 'New Analysis';
                  })()}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-700 bg-slate-900/50 overflow-x-auto">
              {(['overview', 'recommendations', 'keywords', 'pages', 'insights', 'seoGuide'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 sm:px-6 py-4 font-medium transition-all whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-indigo-400 border-b-2 border-indigo-400 bg-slate-800/50'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
                  }`}
                >
                  {(() => {
                    const text = t(`gsc.tab.${tab}`);
                    return text && text !== `gsc.tab.${tab}` ? text : tab.charAt(0).toUpperCase() + tab.slice(1);
                  })()}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6 sm:p-8">
              {activeTab === 'overview' && <OverviewTab data={analysisResult} />}
              {activeTab === 'recommendations' && <RecommendationsTab recommendations={analysisResult.recommendations} />}
              {activeTab === 'keywords' && <KeywordsTab keywords={analysisResult.keywordAnalysis} />}
              {activeTab === 'pages' && <PagesTab pages={analysisResult.pageAnalysis} />}
              {activeTab === 'insights' && <InsightsTab insights={analysisResult.aiInsights} />}
              {activeTab === 'seoGuide' && <SEOGuideTab insights={analysisResult.aiInsights} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{ data: GSCAnalysisResult }> = ({ data }) => {
  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.performanceSummary');
        return text && text !== 'gsc.performanceSummary' ? text : 'Performance Summary';
      })()}</h3>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.totalClicks');
            return text && text !== 'gsc.totalClicks' ? text : 'Total Clicks';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.totalClicks.toLocaleString()}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.totalImpressions');
            return text && text !== 'gsc.totalImpressions' ? text : 'Total Impressions';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.totalImpressions.toLocaleString()}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.averageCTR');
            return text && text !== 'gsc.averageCTR' ? text : 'Average CTR';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.averageCTR}%</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.averagePosition');
            return text && text !== 'gsc.averagePosition' ? text : 'Average Position';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.averagePosition}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.totalKeywords');
            return text && text !== 'gsc.totalKeywords' ? text : 'Total Keywords';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.totalKeywords}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.totalPages');
            return text && text !== 'gsc.totalPages' ? text : 'Total Pages';
          })()}</div>
          <div className="text-2xl font-bold text-white">{data.summary.totalPages}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.topDevice');
            return text && text !== 'gsc.topDevice' ? text : 'Top Device';
          })()}</div>
          <div className="text-lg font-bold text-white">{data.summary.topDevice}</div>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
          <div className="text-slate-400 text-xs font-medium mb-1">{(() => {
            const text = t('gsc.topCountry');
            return text && text !== 'gsc.topCountry' ? text : 'Top Country';
          })()}</div>
          <div className="text-lg font-bold text-white">{data.summary.topCountry}</div>
        </div>
      </div>

      {data.deviceAnalysis && data.deviceAnalysis.length > 0 && (
        <div className="mb-8">
          <h4 className="text-lg font-bold text-white mb-4">{(() => {
            const text = t('gsc.devicePerformance');
            return text && text !== 'gsc.devicePerformance' ? text : 'Device Performance';
          })()}</h4>
          <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                    const text = t('gsc.device');
                    return text && text !== 'gsc.device' ? text : 'Device';
                  })()}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                    const text = t('gsc.clicks');
                    return text && text !== 'gsc.clicks' ? text : 'Clicks';
                  })()}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                    const text = t('gsc.impressions');
                    return text && text !== 'gsc.impressions' ? text : 'Impressions';
                  })()}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">CTR</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                    const text = t('gsc.position');
                    return text && text !== 'gsc.position' ? text : 'Position';
                  })()}</th>
                </tr>
              </thead>
              <tbody>
                {data.deviceAnalysis.map((device: any, index: number) => (
                  <tr key={index} className="border-t border-slate-700">
                    <td className="px-4 py-3 text-white">{device.device}</td>
                    <td className="px-4 py-3 text-slate-300">{device.clicks.toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-300">{device.impressions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-300">{device.ctr.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-slate-300">{device.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Recommendations Tab Component
const RecommendationsTab: React.FC<{ recommendations: any[] }> = ({ recommendations }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  const criticalRecs = recommendations.filter((r: any) => r.priority === 'CRITICAL');
  const highRecs = recommendations.filter((r: any) => r.priority === 'HIGH');
  const mediumRecs = recommendations.filter((r: any) => r.priority === 'MEDIUM');

  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.prioritizedRecommendations');
        return text && text !== 'gsc.prioritizedRecommendations' ? text : 'Prioritized Recommendations';
      })()}</h3>

      {recommendations.length === 0 && (
        <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 text-center">
          <p className="text-slate-400">No recommendations available. Please check the Insights tab for AI-powered recommendations.</p>
        </div>
      )}

      {criticalRecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-bold text-red-400 mb-4">🔴 {(() => {
            const text = t('gsc.criticalPriority');
            return text && text !== 'gsc.criticalPriority' ? text : 'CRITICAL PRIORITY';
          })()}</h4>
          {criticalRecs.map((rec: any, index: number) => (
            <RecommendationCard key={index} recommendation={rec} expanded={expanded === index} onToggle={() => setExpanded(expanded === index ? null : index)} />
          ))}
        </div>
      )}

      {highRecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-bold text-amber-400 mb-4">🟠 {(() => {
            const text = t('gsc.highPriority');
            return text && text !== 'gsc.highPriority' ? text : 'HIGH PRIORITY';
          })()}</h4>
          {highRecs.map((rec: any, index: number) => (
            <RecommendationCard key={index} recommendation={rec} expanded={expanded === criticalRecs.length + index} onToggle={() => setExpanded(expanded === criticalRecs.length + index ? null : criticalRecs.length + index)} />
          ))}
        </div>
      )}

      {mediumRecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-bold text-yellow-400 mb-4">🟡 {(() => {
            const text = t('gsc.mediumPriority');
            return text && text !== 'gsc.mediumPriority' ? text : 'MEDIUM PRIORITY';
          })()}</h4>
          {mediumRecs.map((rec: any, index: number) => (
            <RecommendationCard key={index} recommendation={rec} expanded={expanded === criticalRecs.length + highRecs.length + index} onToggle={() => setExpanded(expanded === criticalRecs.length + highRecs.length + index ? null : criticalRecs.length + highRecs.length + index)} />
          ))}
        </div>
      )}
    </div>
  );
};

// Recommendation Card Component
const RecommendationCard: React.FC<{ recommendation: any; expanded: boolean; onToggle: () => void }> = ({ recommendation, expanded, onToggle }) => {
  return (
    <div className="bg-slate-900/50 border border-slate-700 rounded-xl mb-4 overflow-hidden">
      <div className="p-4 cursor-pointer hover:bg-slate-800/50 transition-all flex items-center justify-between" onClick={onToggle}>
        <div className="flex items-center gap-3 flex-1">
          <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded text-xs font-bold">
            {recommendation.type}
          </span>
          <span className="text-white font-medium flex-1">{recommendation.action}</span>
        </div>
        <button className="text-slate-400 hover:text-white">
          {expanded ? <X size={20} /> : <span className="text-xl">+</span>}
        </button>
      </div>

      {expanded && (
        <div className="p-4 bg-slate-900 border-t border-slate-700">
          {recommendation.keyword && <p className="text-slate-300 mb-2"><strong className="text-white">Keyword:</strong> {recommendation.keyword}</p>}
          {recommendation.page && <p className="text-slate-300 mb-2"><strong className="text-white">Page:</strong> {recommendation.page}</p>}
          {recommendation.currentPosition && <p className="text-slate-300 mb-2"><strong className="text-white">Current Position:</strong> {recommendation.currentPosition.toFixed(1)}</p>}
          {recommendation.impressions && <p className="text-slate-300 mb-2"><strong className="text-white">Impressions:</strong> {recommendation.impressions.toLocaleString()}</p>}
          {recommendation.clicks !== undefined && <p className="text-slate-300 mb-2"><strong className="text-white">Clicks:</strong> {recommendation.clicks.toLocaleString()}</p>}
          {recommendation.ctr !== undefined && <p className="text-slate-300 mb-2"><strong className="text-white">CTR:</strong> {recommendation.ctr.toFixed(2)}%</p>}
          <p className="text-slate-300 mb-4"><strong className="text-white">Why:</strong> {recommendation.reason}</p>
          {recommendation.estimatedImpact && (
            <p className="text-emerald-400 font-bold mb-4">
              <strong>Estimated Impact:</strong> {recommendation.estimatedImpact}
            </p>
          )}
          {recommendation.recommendedActions && recommendation.recommendedActions.length > 0 && (
            <div className="bg-slate-800/50 p-4 rounded-lg">
              <strong className="text-white block mb-2">{(() => {
                const text = t('gsc.actionSteps');
                return text && text !== 'gsc.actionSteps' ? text : 'Action Steps:';
              })()}</strong>
              <ol className="list-decimal list-inside space-y-1 text-slate-300">
                {recommendation.recommendedActions.map((action: string, i: number) => (
                  <li key={i}>{action}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Keywords Tab Component
const KeywordsTab: React.FC<{ keywords: any[] }> = ({ keywords }) => {
  const [showAll, setShowAll] = useState(false);
  const displayedKeywords = showAll ? keywords : keywords.slice(0, 20);

  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.keywordAnalysis');
        return text && text !== 'gsc.keywordAnalysis' ? text : 'Keyword Analysis (Sorted by Opportunity)';
      })()}</h3>
      <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">#</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.keyword');
                return text && text !== 'gsc.keyword' ? text : 'Keyword';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.position');
                return text && text !== 'gsc.position' ? text : 'Position';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.clicks');
                return text && text !== 'gsc.clicks' ? text : 'Clicks';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.impressions');
                return text && text !== 'gsc.impressions' ? text : 'Impressions';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">CTR</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.opportunityScore');
                return text && text !== 'gsc.opportunityScore' ? text : 'Opportunity Score';
              })()}</th>
            </tr>
          </thead>
          <tbody>
            {displayedKeywords.map((keyword: any, index: number) => (
              <tr key={index} className={`border-t border-slate-700 ${index < 5 ? 'bg-amber-500/10' : ''}`}>
                <td className="px-4 py-3 text-slate-300">{index + 1}</td>
                <td className="px-4 py-3 text-indigo-400 font-medium">{keyword.query}</td>
                <td className="px-4 py-3 text-slate-300">{keyword.position.toFixed(1)}</td>
                <td className="px-4 py-3 text-slate-300">{keyword.clicks.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-300">{keyword.impressions.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-300">{keyword.ctr.toFixed(2)}%</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-indigo-500 text-white rounded text-xs font-bold">
                    {keyword.opportunityScore.toFixed(0)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {keywords.length > 20 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-white transition-all"
        >
          {showAll ? (() => {
            const text = t('gsc.showLess');
            return text && text !== 'gsc.showLess' ? text : 'Show Less';
          })() : `${(() => {
            const text = t('gsc.showAll');
            return text && text !== 'gsc.showAll' ? text : 'Show All';
          })()} ${keywords.length} ${(() => {
            const text = t('gsc.keywords');
            return text && text !== 'gsc.keywords' ? text : 'Keywords';
          })()}`}
        </button>
      )}
    </div>
  );
};

// Pages Tab Component
const PagesTab: React.FC<{ pages: any[] }> = ({ pages }) => {
  const [showAll, setShowAll] = useState(false);
  const displayedPages = showAll ? pages : pages.slice(0, 20);

  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.pagePerformance');
        return text && text !== 'gsc.pagePerformance' ? text : 'Page Performance Analysis';
      })()}</h3>
      <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">#</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.page');
                return text && text !== 'gsc.page' ? text : 'Page';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.clicks');
                return text && text !== 'gsc.clicks' ? text : 'Clicks';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.impressions');
                return text && text !== 'gsc.impressions' ? text : 'Impressions';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">CTR</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.position');
                return text && text !== 'gsc.position' ? text : 'Position';
              })()}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">{(() => {
                const text = t('gsc.performanceScore');
                return text && text !== 'gsc.performanceScore' ? text : 'Performance Score';
              })()}</th>
            </tr>
          </thead>
          <tbody>
            {displayedPages.map((page: any, index: number) => (
              <tr key={index} className={`border-t border-slate-700 ${index < 5 ? 'bg-amber-500/10' : ''}`}>
                <td className="px-4 py-3 text-slate-300">{index + 1}</td>
                <td className="px-4 py-3 text-indigo-400 font-medium max-w-xs truncate">{page.page}</td>
                <td className="px-4 py-3 text-slate-300">{page.clicks.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-300">{page.impressions.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-300">{page.ctr.toFixed(2)}%</td>
                <td className="px-4 py-3 text-slate-300">{page.position.toFixed(1)}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-indigo-500 text-white rounded text-xs font-bold">
                    {page.performanceScore.toFixed(0)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages.length > 20 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-white transition-all"
        >
          {showAll ? (() => {
            const text = t('gsc.showLess');
            return text && text !== 'gsc.showLess' ? text : 'Show Less';
          })() : `${(() => {
            const text = t('gsc.showAll');
            return text && text !== 'gsc.showAll' ? text : 'Show All';
          })()} ${pages.length} ${(() => {
            const text = t('gsc.pages');
            return text && text !== 'gsc.pages' ? text : 'Pages';
          })()}`}
        </button>
      )}
    </div>
  );
};

// Insights Tab Component
const InsightsTab: React.FC<{ insights: any }> = ({ insights }) => {
  if (!insights || insights.error) {
    return <div className="text-slate-400">{(() => {
      const text = t('gsc.aiUnavailable');
      return text && text !== 'gsc.aiUnavailable' ? text : 'AI analysis unavailable';
    })()}</div>;
  }

  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.aiInsights');
        return text && text !== 'gsc.aiInsights' ? text : 'AI-Powered Strategic Insights';
      })()}</h3>

      {/* Keywords to Focus On */}
      {insights.keywordsToFocusOn && insights.keywordsToFocusOn.length > 0 && (
        <div className="mb-8 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">🎯 Keywords to Focus On Right Now</h4>
          {insights.keywordsToFocusOn.map((kw: any, index: number) => (
            <div key={index} className="mb-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-start justify-between mb-2">
                <h5 className="text-lg font-bold text-white">{kw.keyword}</h5>
                <div className="text-sm text-slate-400">
                  Position: {kw.currentPosition?.toFixed(1)} | CTR: {kw.currentCTR?.toFixed(2)}% | Clicks: {kw.currentClicks} | Impressions: {kw.currentImpressions}
                </div>
              </div>
              <p className="text-slate-300 mb-3">{kw.whyFocus}</p>
              <div className="mb-3">
                <span className="text-emerald-400 font-bold">Potential Gain: </span>
                <span className="text-white">{kw.potentialTrafficGain}</span>
              </div>
              {kw.specificActions && kw.specificActions.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-slate-400 mb-2">Specific Actions:</div>
                  <ul className="space-y-1">
                    {kw.specificActions.map((action: string, i: number) => (
                      <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                        <span className="text-indigo-400 mt-1">•</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {kw.expectedImpact && (
                <div className="mt-3 text-sm text-slate-400">
                  <strong>Expected Impact:</strong> {kw.expectedImpact}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lost Opportunities */}
      {insights.lostOpportunities && insights.lostOpportunities.length > 0 && (
        <div className="mb-8 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
          <h4 className="text-lg font-bold text-red-400 mb-4">⚠️ Lost Opportunities</h4>
          {insights.lostOpportunities.map((opp: any, index: number) => (
            <div key={index} className="mb-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-start justify-between mb-2">
                <h5 className="text-lg font-bold text-white">{opp.item}</h5>
                <div className="text-red-400 font-bold">
                  Lost: ~{opp.lostClicksPerMonth} clicks/month
                </div>
              </div>
              <div className="text-sm text-slate-400 mb-2">{opp.currentMetrics}</div>
              <p className="text-slate-300 mb-2"><strong>Why losing:</strong> {opp.whyLosing}</p>
              <p className="text-slate-300 mb-2"><strong>Root cause:</strong> {opp.rootCause}</p>
              <div className="mb-2">
                <span className="text-emerald-400 font-bold">Fix: </span>
                <span className="text-white">{opp.fix}</span>
              </div>
              <div className="text-emerald-400">
                <strong>Potential Gain:</strong> {opp.potentialGain}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Traffic Gap Analysis */}
      {insights.trafficGapAnalysis && (
        <div className="mb-8 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl p-6">
          <h4 className="text-lg font-bold text-amber-400 mb-4">📊 Traffic Gap Analysis</h4>
          <p className="text-slate-300 mb-3">{insights.trafficGapAnalysis.currentVsBenchmark}</p>
          {insights.trafficGapAnalysis.estimatedPotentialTraffic && (
            <div className="mb-3">
              <span className="text-emerald-400 font-bold">Estimated Potential Traffic: </span>
              <span className="text-white">{insights.trafficGapAnalysis.estimatedPotentialTraffic}</span>
            </div>
          )}
          {insights.trafficGapAnalysis.priorityGaps && insights.trafficGapAnalysis.priorityGaps.length > 0 && (
            <div>
              <div className="text-sm font-bold text-slate-400 mb-2">Priority Gaps to Fill:</div>
              <ul className="space-y-1">
                {insights.trafficGapAnalysis.priorityGaps.map((gap: string, i: number) => (
                  <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>{gap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {insights.keyFindings && insights.keyFindings.length > 0 && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-white mb-4">🔍 {(() => {
            const text = t('gsc.keyFindings');
            return text && text !== 'gsc.keyFindings' ? text : 'Key Findings';
          })()}</h4>
          <ul className="space-y-2 text-slate-300">
            {insights.keyFindings.map((finding: string, index: number) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-indigo-400 mt-1">•</span>
                <span>{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {insights.quickWins && insights.quickWins.length > 0 && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-white mb-4">⚡ {(() => {
            const text = t('gsc.quickWins');
            return text && text !== 'gsc.quickWins' ? text : 'Quick Wins';
          })()}</h4>
          <ul className="space-y-2 text-slate-300">
            {insights.quickWins.map((win: string, index: number) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-emerald-400 mt-1">•</span>
                <span>{win}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {insights.strategicRecommendations && insights.strategicRecommendations.length > 0 && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-white mb-4">📋 {(() => {
            const text = t('gsc.strategicRecommendations');
            return text && text !== 'gsc.strategicRecommendations' ? text : 'Strategic Recommendations';
          })()}</h4>
          <ol className="space-y-2 text-slate-300 list-decimal list-inside">
            {insights.strategicRecommendations.map((rec: string, index: number) => (
              <li key={index}>{rec}</li>
            ))}
          </ol>
        </div>
      )}

      {insights.keywordStrategy && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-white mb-4">🔑 {t('gsc.keywordStrategy') || 'Keyword Strategy'}</h4>
          {insights.keywordStrategy.strengthen && (
            <div className="mb-4">
              <strong className="text-white block mb-2">{t('gsc.strengthen') || 'Strengthen:'}</strong>
              <ul className="space-y-1 text-slate-300">
                {insights.keywordStrategy.strengthen.map((kw: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>{kw}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {insights.keywordStrategy.expand && (
            <div className="mb-4">
              <strong className="text-white block mb-2">{t('gsc.expand') || 'Expand:'}</strong>
              <ul className="space-y-1 text-slate-300">
                {insights.keywordStrategy.expand.map((kw: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>{kw}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// SEO Guide Tab Component
const SEOGuideTab: React.FC<{ insights: any }> = ({ insights }) => {
  if (!insights || insights.error || !insights.seoGuide) {
    return (
      <div className="text-slate-400">
        {t('gsc.seoGuideUnavailable') || 'SEO Guide will be available after analysis.'}
      </div>
    );
  }

  const guide = insights.seoGuide;

  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-6">{(() => {
        const text = t('gsc.seoGuideTitle');
        return text && text !== 'gsc.seoGuideTitle' ? text : 'Complete SEO Guide & Recommendations';
      })()}</h3>

      {guide.onPageOptimization && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">📄 {(() => {
            const text = t('gsc.onPageSEO');
            return text && text !== 'gsc.onPageSEO' ? text : 'On-Page Optimization';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.onPageOptimization}</p>
        </div>
      )}

      {guide.technicalSEO && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">⚙️ {(() => {
            const text = t('gsc.technicalSEO');
            return text && text !== 'gsc.technicalSEO' ? text : 'Technical SEO';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.technicalSEO}</p>
        </div>
      )}

      {guide.contentStrategy && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">✍️ {(() => {
            const text = t('gsc.contentStrategy');
            return text && text !== 'gsc.contentStrategy' ? text : 'Content Strategy';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.contentStrategy}</p>
        </div>
      )}

      {guide.linkBuilding && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">🔗 {(() => {
            const text = t('gsc.linkBuilding');
            return text && text !== 'gsc.linkBuilding' ? text : 'Link Building';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.linkBuilding}</p>
        </div>
      )}

      {guide.mobileOptimization && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">📱 {(() => {
            const text = t('gsc.mobileOptimization');
            return text && text !== 'gsc.mobileOptimization' ? text : 'Mobile Optimization';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.mobileOptimization}</p>
        </div>
      )}

      {guide.localSEO && (
        <div className="mb-6 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
          <h4 className="text-lg font-bold text-indigo-400 mb-4">📍 {(() => {
            const text = t('gsc.localSEO');
            return text && text !== 'gsc.localSEO' ? text : 'Local SEO';
          })()}</h4>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{guide.localSEO}</p>
        </div>
      )}

      {insights.actionPlan && (
        <div className="mt-8 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6">
          <h4 className="text-lg font-bold text-white mb-4">📋 {(() => {
            const text = t('gsc.actionPlan');
            return text && text !== 'gsc.actionPlan' ? text : 'Action Plan';
          })()}</h4>
          
          {insights.actionPlan.immediate && insights.actionPlan.immediate.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-bold text-red-400 mb-2">🚨 {t('gsc.immediate') || 'Immediate (This Week)'}</h5>
              <ul className="space-y-1 text-slate-300">
                {insights.actionPlan.immediate.map((action: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.actionPlan.shortTerm && insights.actionPlan.shortTerm.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-bold text-amber-400 mb-2">⏰ {t('gsc.shortTerm') || 'Short Term (Next Month)'}</h5>
              <ul className="space-y-1 text-slate-300">
                {insights.actionPlan.shortTerm.map((action: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.actionPlan.longTerm && insights.actionPlan.longTerm.length > 0 && (
            <div>
              <h5 className="text-sm font-bold text-emerald-400 mb-2">🎯 {t('gsc.longTerm') || 'Long Term (3-6 Months)'}</h5>
              <ul className="space-y-1 text-slate-300">
                {insights.actionPlan.longTerm.map((action: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-400 mt-1">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GSCAnalyzer;

